import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import EventSummaryCard from '../bookings/EventSummaryCard';
import VenueCard from '../bookings/VenueCard';
import CateringCard from '../bookings/CateringCard';
import SponsorCard from '../bookings/SponsorCard';
import PropTypes from 'prop-types';

const CARD_HEIGHT = 340;

const EventRecommendationRow = ({ recommendation = {}, onView }) => {
  // Early return if no recommendation data
  if (!recommendation || !recommendation.venue) {
    return (
      <Paper elevation={0} sx={{
        border: '1.5px solid #e0e0e0',
        borderRadius: 4,
        p: { xs: 2, sm: 3 },
        mb: 5,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No recommendation data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Venue
  const venue = recommendation.venue;
  const venueCardProps = {
    name: venue.title,
    image: venue.coverPhoto,
    stats: [
      // { icon: <span role="img" aria-label="star">⭐</span>, value: venue.rating || '5.00' },
      { icon: <span role="img" aria-label="guests">👥</span>, value: venue.capacity?.seated || venue.capacity?.standing || 'N/A' },
    ],
    links: [
      ...(venue.website ? [{ url: venue.website, icon: <span role="img" aria-label="website">🌐</span> }] : []),
      ...(venue.contact?.instagram ? [{ url: venue.contact.instagram, icon: <span role="img" aria-label="instagram">📸</span> }] : []),
    ]
  };

  // Catering (find first catering addon)
  const cateringAddon = (venue.addons || []).find(a => (a.addonType || '').toLowerCase().includes('cater'));
  const catering = cateringAddon ? {
    name: cateringAddon.companyName || 'In-House Catering',
    image: (Array.isArray(cateringAddon.company) && cateringAddon.company[0]?.logoUrl) || undefined,
    stats: [
      // { icon: <span role="img" aria-label="star">⭐</span>, value: cateringAddon.rating || '5.00' },
      { icon: <span role="img" aria-label="price">💲</span>, value: cateringAddon.price ? `$${cateringAddon.price}` : 'N/A' },
    ],
    links: [
      ...(Array.isArray(cateringAddon.company) && cateringAddon.company[0]?.website ? [{ url: cateringAddon.company[0].website, icon: <span role="img" aria-label="website">🌐</span> }] : []),
    ]
  } : null;

  // Sponsor (show first sponsor)
  const sponsorObj = (venue.sponsors && venue.sponsors.length > 0) ? venue.sponsors[0] : null;
  const sponsor = sponsorObj ? {
    name: sponsorObj.companyName || (Array.isArray(sponsorObj.company) && sponsorObj.company[0]?.name) || 'Sponsor',
    image: (Array.isArray(sponsorObj.company) && sponsorObj.company[0]?.logoUrl) || undefined,
    stats: [
      { icon: <span role="img" aria-label="plus">➕</span>, value: sponsorObj.price ? `+$${sponsorObj.price}` : '+$1,000' },
    ],
    links: [
      ...(Array.isArray(sponsorObj.company) && sponsorObj.company[0]?.website ? [{ url: sponsorObj.company[0].website, icon: <span role="img" aria-label="website">🌐</span> }] : []),
    ]
  } : null;

  return (
    <Paper elevation={0} sx={{
      border: '1.5px solid #e0e0e0',
      borderRadius: 4,
      p: { xs: 2, sm: 3 },
      mb: 5,
      bgcolor: 'background.paper',
      overflow: 'hidden',
    }}>
      <Box
        role="group"
        aria-label="Event recommendation row"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 3,
          width: '100%',
          overflowX: 'auto',
          pb: 2,
        }}
      >
        {/* Event Summary */}
        <Box role="region" aria-label="Event summary" sx={{ height: CARD_HEIGHT, display: 'flex', alignItems: 'stretch' }}>
          <EventSummaryCard
            title={recommendation.title}
            location={venue.neighborhood + ", " + venue.city + ", " + venue.state  || venue.address}
            price={venue.price ? `$${venue.price.toLocaleString()}` : '$12,200'}
            onView={() => onView && onView(recommendation)}
            height={CARD_HEIGHT}
          />
        </Box>
        {/* Venue */}
        <Box role="region" aria-label="Venue details" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, height: CARD_HEIGHT }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, ml: 1 }}>
            VENUE
          </Typography>
          <VenueCard {...venueCardProps} height={CARD_HEIGHT} />
        </Box>
        {/* Catering */}
        <Box role="region" aria-label="Catering details" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, height: CARD_HEIGHT }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, ml: 1 }}>
            CATERING
          </Typography>
          {catering ? (
            <CateringCard {...catering} height={CARD_HEIGHT} />
          ) : (
            <Box sx={{ height: CARD_HEIGHT, minWidth: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc', borderRadius: 4, color: 'text.secondary', width: '100%', fontSize: 22, fontWeight: 500 }}>
              Catering Not Included
            </Box>
          )}
        </Box>
        {/* Sponsor */}
        <Box role="region" aria-label="Sponsor details" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, height: CARD_HEIGHT }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, ml: 1 }}>
            SPONSOR
          </Typography>
          {sponsor ? (
            <SponsorCard {...sponsor} height={CARD_HEIGHT} />
          ) : (
            <Box sx={{ height: CARD_HEIGHT, minWidth: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc', borderRadius: 4, color: 'text.secondary', width: '100%', fontSize: 22, fontWeight: 500 }}>
              Sponsor Not Included
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

EventRecommendationRow.propTypes = {
  recommendation: PropTypes.shape({
    venue: PropTypes.shape({
      title: PropTypes.string,
      coverPhoto: PropTypes.string,
      capacity: PropTypes.shape({
        seated: PropTypes.number,
        standing: PropTypes.number
      }),
      website: PropTypes.string,
      contact: PropTypes.shape({
        instagram: PropTypes.string
      }),
      addons: PropTypes.array,
      sponsors: PropTypes.array
    }),
    title: PropTypes.string
  }),
  onView: PropTypes.func
};

export default EventRecommendationRow; 